import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Home, FileText, Upload, Trash2, Download, Filter, Edit3, Copy, X,
} from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { formatApiError } from '@/utils/apiError';

const BRANDS = [
  { key: 'probound', label: 'ProBound', color: '#2F5E96' },
  { key: 'proepoxy', label: 'ProEpoxy', color: '#7A0B7A' },
  { key: 'prodecor', label: 'ProDecor', color: '#D4A24B' },
];

const CATEGORIES = [
  { key: 'quote_letter',   label: 'Quote Letters' },
  { key: 'rams',           label: 'RAMS' },
  { key: 'customer_email', label: 'Customer Email' },
  { key: 'tcs',            label: 'Terms & Conditions' },
  { key: 'warranty',       label: 'Warranty' },
  { key: 'aftercare',      label: 'Aftercare' },
];

const emptyForm = {
  brand: 'probound',
  category: 'quote_letter',
  title: '',
  description: '',
  body_text: '',
  file: null,
};

export default function StaffTemplates() {
  const navigate = useNavigate();
  const { isStaff } = useContext(AuthContext);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandFilter, setBrandFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null); // template being edited

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (brandFilter) qs.set('brand', brandFilter);
      if (categoryFilter) qs.set('category', categoryFilter);
      const url = `${API_URL}/api/templates${qs.toString() ? `?${qs.toString()}` : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load templates');
      setTemplates(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandFilter, categoryFilter]);

  useEffect(() => {
    if (!isStaff) { navigate('/'); return; }
    load();
  }, [isStaff, navigate, load]);

  const submitUpload = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.file && !form.body_text.trim()) {
      toast.error('Provide a file, body text, or both');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('brand', form.brand);
      fd.append('category', form.category);
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('body_text', form.body_text);
      if (form.file) fd.append('file', form.file);
      const res = await fetch(`${API_URL}/api/admin/templates`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Upload failed');
      toast.success('Template saved');
      setForm({ ...emptyForm, brand: form.brand, category: form.category });
      const fileInput = document.getElementById('st-file');
      if (fileInput) fileInput.value = '';
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  const saveEdit = async () => {
    if (!editing.title.trim()) { toast.error('Title is required'); return; }
    try {
      const res = await fetch(`${API_URL}/api/admin/templates/${editing.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editing.title,
          description: editing.description,
          body_text: editing.body_text,
          brand: editing.brand,
          category: editing.category,
        }),
      });
      if (!res.ok) throw new Error(formatApiError(await res.json()) || 'Update failed');
      toast.success('Updated');
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = async (tmpl) => {
    if (!window.confirm(`Delete "${tmpl.title}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/templates/${tmpl.id}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Deleted');
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const copyText = async (t) => {
    try {
      await navigator.clipboard.writeText(t.body_text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #7A0B7A 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/staff')} className="p-2 hover:bg-white/10 rounded-lg" data-testid="st-back" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#D4A24B' }} />
              Templates
            </h1>
            <p className="hidden sm:block text-white/70 text-xs sm:text-sm mt-0.5">
              Quote letters, RAMS, customer emails, T&amp;Cs, warranty and aftercare docs — per brand.
            </p>
          </div>
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20" data-testid="st-home">
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Staff</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-5 grid lg:grid-cols-[360px_1fr] gap-4">
        {/* Upload form */}
        <Card className="p-4 h-fit">
          <h2 className="font-bold text-sm uppercase tracking-wider text-gray-700 mb-3 flex items-center gap-2">
            <Upload className="w-4 h-4" /> New Template
          </h2>
          <form onSubmit={submitUpload} className="space-y-3" data-testid="st-upload-form">
            <div>
              <Label className="text-xs font-semibold">Brand</Label>
              <div className="grid grid-cols-3 gap-1 mt-1">
                {BRANDS.map((b) => (
                  <button
                    type="button"
                    key={b.key}
                    data-testid={`st-brand-${b.key}`}
                    onClick={() => setForm({ ...form, brand: b.key })}
                    className={`text-xs font-semibold py-1.5 rounded-md border-2 transition-colors ${
                      form.brand === b.key ? 'text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                    }`}
                    style={form.brand === b.key ? { background: b.color, borderColor: b.color } : {}}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold" htmlFor="st-category">Category</Label>
              <select
                id="st-category"
                data-testid="st-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border rounded-md px-3 py-2 text-sm mt-1"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs font-semibold" htmlFor="st-title">Title</Label>
              <Input id="st-title" data-testid="st-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Standard Quote Cover Letter" className="h-9" />
            </div>
            <div>
              <Label className="text-xs font-semibold" htmlFor="st-desc">Description (optional)</Label>
              <Textarea id="st-desc" data-testid="st-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="text-sm" placeholder="Short summary shown on the tile" />
            </div>
            <div>
              <Label className="text-xs font-semibold" htmlFor="st-body">Body text (optional — for copy &amp; paste)</Label>
              <Textarea id="st-body" data-testid="st-body" value={form.body_text} onChange={(e) => setForm({ ...form, body_text: e.target.value })} rows={5} className="text-sm font-mono" placeholder="Paste the email or letter copy installers should be able to copy…" />
            </div>
            <div>
              <Label className="text-xs font-semibold" htmlFor="st-file">Attach file (optional, max 50 MB)</Label>
              <input
                id="st-file"
                data-testid="st-file"
                type="file"
                onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                className="block w-full text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#7A0B7A]/10 file:text-[#7A0B7A] hover:file:bg-[#7A0B7A]/20"
              />
              <p className="text-[10px] text-gray-500 mt-1">PDF, DOC(X), XLS(X), PPT(X), TXT, RTF, JPG, PNG, WebP, ZIP.</p>
            </div>
            <Button type="submit" disabled={uploading} className="w-full bg-[#7A0B7A] hover:bg-[#5a085a] text-white" data-testid="st-submit">
              <Upload className="w-4 h-4 mr-1.5" /> {uploading ? 'Saving…' : 'Save Template'}
            </Button>
          </form>
        </Card>

        {/* Library */}
        <div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Filter className="w-4 h-4 text-gray-500" />
            <button data-testid="st-brand-filter-all" onClick={() => setBrandFilter('')} className={`text-xs px-3 py-1.5 rounded-full border-2 ${brandFilter === '' ? 'border-[#7A0B7A] bg-[#7A0B7A] text-white font-semibold' : 'border-gray-200 bg-white text-gray-600'}`}>All brands</button>
            {BRANDS.map((b) => (
              <button
                key={b.key}
                data-testid={`st-brand-filter-${b.key}`}
                onClick={() => setBrandFilter(b.key)}
                className={`text-xs px-3 py-1.5 rounded-full border-2 ${brandFilter === b.key ? 'text-white font-semibold' : 'border-gray-200 bg-white text-gray-600'}`}
                style={brandFilter === b.key ? { background: b.color, borderColor: b.color } : {}}
              >
                {b.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <button data-testid="st-cat-filter-all" onClick={() => setCategoryFilter('')} className={`text-xs px-3 py-1 rounded-full border ${categoryFilter === '' ? 'border-gray-900 bg-gray-900 text-white font-semibold' : 'border-gray-200 bg-white text-gray-600'}`}>All categories</button>
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                data-testid={`st-cat-filter-${c.key}`}
                onClick={() => setCategoryFilter(c.key)}
                className={`text-xs px-3 py-1 rounded-full border ${categoryFilter === c.key ? 'border-gray-900 bg-gray-900 text-white font-semibold' : 'border-gray-200 bg-white text-gray-600'}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-16 text-gray-500">Loading…</div>
          ) : templates.length === 0 ? (
            <div className="text-center py-16 text-gray-400 italic border-2 border-dashed border-gray-200 rounded-xl">
              No templates yet. Add one to get started.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="st-template-grid">
              {templates.map((t) => {
                const b = BRANDS.find((x) => x.key === t.brand) || { label: t.brand, color: '#666' };
                const c = CATEGORIES.find((x) => x.key === t.category) || { label: t.category };
                return (
                  <Card key={t.id} className="p-4 flex flex-col gap-2" data-testid={`st-template-${t.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-bold uppercase text-white px-2 py-0.5 rounded-full" style={{ background: b.color }}>{b.label}</span>
                      <span className="text-[10px] font-bold uppercase text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">{c.label}</span>
                    </div>
                    <h3 className="font-semibold text-sm" title={t.title}>{t.title}</h3>
                    {t.description && <p className="text-xs text-gray-500 line-clamp-2">{t.description}</p>}
                    {t.body_text && (
                      <details className="text-xs text-gray-600 bg-gray-50 rounded-md px-2 py-1">
                        <summary className="cursor-pointer font-medium">Preview body text</summary>
                        <pre className="whitespace-pre-wrap mt-1 font-sans text-[11px] leading-snug max-h-40 overflow-auto">{t.body_text}</pre>
                      </details>
                    )}
                    {t.file_name && (
                      <p className="text-[10px] text-gray-400">{t.file_name} · {(t.file_size / 1024).toFixed(0)} KB</p>
                    )}
                    <div className="flex gap-1.5 mt-1 flex-wrap">
                      {t.file_url && (
                        <a href={t.file_url} download target="_blank" rel="noopener noreferrer">
                          <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`st-download-${t.id}`}>
                            <Download className="w-3.5 h-3.5 mr-1" /> Open
                          </Button>
                        </a>
                      )}
                      {t.body_text && (
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => copyText(t)} data-testid={`st-copy-${t.id}`}>
                          <Copy className="w-3.5 h-3.5 mr-1" /> Copy text
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => setEditing({ ...t })} data-testid={`st-edit-${t.id}`}>
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 px-2 border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => remove(t)} data-testid={`st-delete-${t.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2"><Edit3 className="w-5 h-5" /> Edit Template</h2>
              <button onClick={() => setEditing(null)} className="p-1 hover:bg-gray-100 rounded" data-testid="st-edit-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Brand</Label>
                  <select value={editing.brand} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} className="w-full border rounded-md px-3 py-2 text-sm mt-1" data-testid="st-edit-brand">
                    {BRANDS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Category</Label>
                  <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full border rounded-md px-3 py-2 text-sm mt-1" data-testid="st-edit-category">
                    {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Title</Label>
                <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="st-edit-title" />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea rows={2} value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="st-edit-desc" />
              </div>
              <div>
                <Label className="text-xs">Body text</Label>
                <Textarea rows={8} className="font-mono text-sm" value={editing.body_text || ''} onChange={(e) => setEditing({ ...editing, body_text: e.target.value })} data-testid="st-edit-body" />
              </div>
              {editing.file_name && (
                <p className="text-xs text-gray-500">Attached file: <span className="font-medium">{editing.file_name}</span> (re-upload not supported here — delete and recreate to change the file).</p>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button onClick={saveEdit} className="bg-[#7A0B7A] hover:bg-[#5a085a] text-white" data-testid="st-edit-save">Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
